import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ResumableUploadResponse,
  UploadSession,
} from '../models/upload-session.model';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { getApiHostUrl } from '../../util/const';

import * as tus from 'tus-js-client';

export interface Upload {
  id: number;
  filename: string;
  progress: number;
  status: 'uploading' | 'completed' | 'failed';
}

export interface AddUploadOptions {
  file: File;
  uploadURI: string;
  trackById?: string;
  bunnyDetails?: UploadSession['bunnyDetails'];
  protocol?: 'bunny-tus' | 'bunny-storage-http' | 'gcs-resumable';
}

@Injectable({
  providedIn: 'root',
})
export class CloudUploadService {
  private http = inject(HttpClient);

  private uploads = new BehaviorSubject<UploadSession[]>([]);
  uploads$ = this.uploads.asObservable();
  private idCounter = 0;
  private tusUploads = new Map<number, tus.Upload>();

  addUploadFromResponse(
    file: File,
    response: ResumableUploadResponse,
    trackById?: string,
  ): number {
    return this.addUpload({
      file,
      uploadURI: response.uploadUrl,
      trackById: trackById || response.resourceId,
      protocol: response.protocol,
      bunnyDetails:
        response.protocol === 'bunny-tus' ? response.bunnyDetails : undefined,
    });
  }

  addUpload(options: AddUploadOptions): number {
    const { file, uploadURI, trackById, bunnyDetails, protocol } = options;

    const uploadSession: UploadSession = {
      id: this.idCounter++,
      file,
      startByte: 0,
      chunkSize: 8 * 1024 * 1024, // 8 MiB optimal chunk size (multiple of 256 KiB for GCS)
      uploadProgress: 0,
      uploadURI,
      autoStart: true,
      uploadStatus: 'uploading',
      trackById,
      protocol: protocol || 'gcs-resumable',
      bunnyDetails,
    };

    this.uploads.next([...this.uploads.value, uploadSession]);
    this.uploadChunk(uploadSession);

    return uploadSession.id;
  }

  updateUpload(updatedUpload: UploadSession) {
    const uploads = this.uploads.value.map((upload) =>
      upload.id === updatedUpload.id ? updatedUpload : upload,
    );
    this.uploads.next(uploads);
  }

  removeUpload(id: number) {
    this.uploads.next(this.uploads.value.filter((upload) => upload.id !== id));
  }

  cancelUpload(id: number) {
    const session = this.getUploadSession(id);
    if (session) {
      session.uploadStatus = 'cancelled';
      this.updateUpload(session);
      const tusUpload = this.tusUploads.get(id);
      if (tusUpload) {
        tusUpload.abort();
        this.tusUploads.delete(id);
      }
    }
  }

  retryUpload(id: number) {
    const session = this.getUploadSession(id);
    if (session) {
      session.startByte = 0;
      session.uploadStatus = 'uploading';
      session.uploadProgress = 0;
      this.updateUpload(session);
      if (session.protocol === 'bunny-tus' || session.bunnyDetails) {
        this.tusUploads.delete(id);
      }
      this.uploadChunk(session);
    }
  }

  pauseUpload(id: number) {
    const session = this.getUploadSession(id);
    if (session && session.uploadStatus === 'uploading') {
      session.uploadStatus = 'paused';
      this.updateUpload(session);
      const tusUpload = this.tusUploads.get(id);
      if (tusUpload) {
        tusUpload.abort();
      }
    }
  }

  resumeUpload(id: number) {
    const session = this.getUploadSession(id);
    if (session && session.uploadStatus === 'paused') {
      session.uploadStatus = 'uploading';
      this.updateUpload(session);
      if (session.protocol === 'bunny-tus' || session.bunnyDetails) {
        const tusUpload = this.tusUploads.get(id);
        if (tusUpload) {
          tusUpload.start();
        } else {
          this.uploadBunnyTusChunk(session);
        }
      } else {
        this.uploadChunk(session);
      }
    }
  }

  uploadBunnyStorageHttpChunk(session: UploadSession) {
    if (session.uploadStatus !== 'uploading') return;

    let targetUrl = session.uploadURI;
    if (targetUrl.startsWith('/')) {
      const host = getApiHostUrl();
      targetUrl = host ? `${host}${targetUrl}` : targetUrl;
    }

    (
      this.http.put(targetUrl, session.file, {
        observe: 'events',
        reportProgress: true,
        headers: {
          'Content-Type': session.file.type || 'application/octet-stream',
        },
      }) as Observable<HttpEvent<any>>
    ).subscribe(
      (res: HttpEvent<any>) => {
        if (res.type === HttpEventType.UploadProgress && res.total) {
          session.uploadProgress = Math.round((res.loaded / res.total) * 100);
          this.updateUpload(session);
        } else if (
          res.type === HttpEventType.Response &&
          (res.status === 200 || res.status === 201)
        ) {
          session.uploadStatus = 'completed';
          session.uploadProgress = 100;
          this.updateUpload(session);
        }
      },
      (err: any) => {
        console.error('Bunny Storage HTTP upload error:', err);
        session.uploadStatus = 'failed';
        this.updateUpload(session);
      },
    );
  }

  uploadBunnyTusChunk(session: UploadSession) {
    if (session.uploadStatus !== 'uploading' || !session.bunnyDetails) return;

    const existingTus = this.tusUploads.get(session.id);
    if (existingTus) {
      existingTus.start();
      return;
    }

    const { endpoint, libraryId, videoId, signature, expires } =
      session.bunnyDetails;

    if (
      !endpoint ||
      !libraryId ||
      !videoId ||
      !signature ||
      expires === undefined
    ) {
      session.uploadStatus = 'failed';
      this.updateUpload(session);
      return;
    }

    const tusUpload = new tus.Upload(session.file, {
      endpoint: endpoint,
      retryDelays: [0, 3000, 5000, 10000, 20000, 60000],
      headers: {
        AuthorizationSignature: signature,
        AuthorizationExpire: expires.toString(),
        VideoId: videoId,
        LibraryId: libraryId,
      },
      metadata: {
        filetype: session.file.type || 'video/mp4',
        title: session.file.name,
      },
      onError: (error: any) => {
        console.error('Bunny TUS upload error:', error);
        session.uploadStatus = 'failed';
        this.updateUpload(session);
        this.tusUploads.delete(session.id);
      },
      onProgress: (bytesUploaded: number, bytesTotal: number) => {
        if (bytesTotal) {
          session.uploadProgress = Math.round(
            (bytesUploaded / bytesTotal) * 100,
          );
          this.updateUpload(session);
        }
      },
      onSuccess: () => {
        session.uploadStatus = 'completed';
        session.uploadProgress = 100;
        this.updateUpload(session);
        this.tusUploads.delete(session.id);
      },
    });

    this.tusUploads.set(session.id, tusUpload);

    tusUpload
      .findPreviousUploads()
      .then((previousUploads) => {
        if (previousUploads.length) {
          tusUpload.resumeFromPreviousUpload(previousUploads[0]);
        }
        tusUpload.start();
      })
      .catch(() => {
        tusUpload.start();
      });
  }

  uploadChunk(session: UploadSession) {
    if (session.uploadStatus !== 'uploading') return;

    if (session.protocol === 'bunny-tus' || session.bunnyDetails) {
      this.uploadBunnyTusChunk(session);
      return;
    }

    if (session.protocol === 'bunny-storage-http') {
      this.uploadBunnyStorageHttpChunk(session);
      return;
    }

    let endByte = 0;
    if (session.chunkSize + session.startByte > session.file.size) {
      endByte = session.file.size;
    } else {
      endByte = session.startByte + session.chunkSize;
    }

    //upload chunk
    (
      this.http.put(
        session.uploadURI,
        session.file.slice(session.startByte, endByte),
        {
          observe: 'events',
          reportProgress: true,
          headers: {
            'Content-Range': `bytes ${session.startByte}-${endByte - 1}/${
              session.file.size
            }`,
          },
        },
      ) as Observable<HttpEvent<any>>
    ).subscribe(
      (res: HttpEvent<any>) => {
        if (res.type === HttpEventType.Response && res.status == 200) {
          session.uploadStatus = 'completed';
          session.uploadProgress = 100;
          this.updateUpload(session);
        }
      },
      async (res: any) => {
        if (res.status == 308) {
          const range = res.headers?.get?.('range') || res.headers?.range;
          if (range) {
            const lastByte = +range.substring(range.indexOf('-') + 1);
            session.startByte = lastByte + 1;
            session.uploadProgress =
              (session.startByte / session.file.size) * 100;
            this.updateUpload(session);
          }

          if (session.uploadStatus === 'uploading') {
            this.uploadChunk(session);
          }
        } else {
          session.uploadStatus = 'failed';
          this.updateUpload(session);
          console.log('HTTP Error Occurred!');
        }
      },
    );
  }

  getUploadSession(id: number): UploadSession | undefined {
    return this.uploads.value.find((session) => session.id === id);
  }

  getUploadSessionByTrackingId(trackById: string): UploadSession | undefined {
    return this.uploads.value.find(
      (session) => session.trackById === trackById,
    );
  }
}
